/**
 * Browser capability check.
 *
 * The validator runs computer vision on the user's device, which needs a few
 * modern APIs. When they are missing we must say so in plain language — never
 * show a stack trace, and never pretend the check ran.
 */

export interface SupportReport {
  supported: boolean;
  /** Short user-facing reason, no technical jargon. */
  reason?: string;
  /** Missing capabilities, for the debug panel. */
  missing: string[];
  /** What still works without those capabilities. */
  degraded: string[];
}

export const BROWSER_SUPPORT_MESSAGE =
  'Этот браузер не поддерживает локальную проверку изображения. Попробуйте Chrome или Safari последней версии.';

export const checkBrowserSupport = (): SupportReport => {
  const missing: string[] = [];
  const degraded: string[] = [];

  if (typeof window === 'undefined') {
    return { supported: false, reason: BROWSER_SUPPORT_MESSAGE, missing: ['window'], degraded: [] };
  }

  if (typeof WebAssembly === 'undefined') {
    missing.push('WebAssembly');
  }
  if (typeof createImageBitmap !== 'function') {
    missing.push('createImageBitmap');
  }
  const hasOffscreen = typeof OffscreenCanvas !== 'undefined';
  const hasCanvas = typeof document.createElement('canvas').getContext === 'function';
  if (!hasOffscreen && !hasCanvas) {
    missing.push('canvas-2d');
  } else if (!hasOffscreen) {
    degraded.push('OffscreenCanvas: часть анализа качества может быть недоступна');
  }
  if (typeof Worker === 'undefined') {
    degraded.push('Web Worker: анализ выполняется в основном потоке');
  }
  if (!('PointerEvent' in window)) {
    degraded.push('Pointer Events: выделение проверок касанием может работать иначе');
  }
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    degraded.push('камера: съёмка внутри валидатора недоступна');
  }

  if (missing.length > 0) {
    return {
      supported: false,
      reason: BROWSER_SUPPORT_MESSAGE,
      missing,
      degraded,
    };
  }
  return { supported: true, missing, degraded };
};

/**
 * Maps an unexpected error to a message safe to show a user.
 *
 * Our own rejections (`ImageRejectedError`) are already written for humans;
 * anything else is reported generically, with the raw text kept only for the
 * debug panel.
 */
export const userFacingError = (
  error: unknown,
  options: { known?: boolean } = {},
): { message: string; technical: string } => {
  const technical =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? 'неизвестная ошибка');

  if (options.known) return { message: technical, technical };

  if (
    technical.includes('OffscreenCanvas') ||
    technical.includes('createImageBitmap') ||
    technical.includes('WebAssembly')
  ) {
    return { message: BROWSER_SUPPORT_MESSAGE, technical };
  }
  if (technical.includes('decode') || technical.includes('декодир')) {
    return {
      message: 'Не удалось прочитать изображение. Возможно, файл повреждён или это не фотография.',
      technical,
    };
  }
  if (technical.toLowerCase().includes('memory') || technical.includes('allocation')) {
    return {
      message: 'Изображение слишком большое для обработки в браузере. Попробуйте файл меньшего разрешения.',
      technical,
    };
  }
  return {
    message:
      'Не удалось выполнить проверку в браузере. Обновите страницу или попробуйте другое фото.',
    technical,
  };
};
